import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { serviceIcons, serviceSlugs, CONTACT } from "@/i18n/content";
import { Reveal, MaskedLines } from "./Reveal";

const MotionLink = motion.create(Link);

export const Services = () => {
  const { t } = useLang();
  const s = t.services;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-surface py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline mb-6">{s.overline}</p>
            </Reveal>
            <h2 className="heading-serif whitespace-pre-line text-4xl leading-[1.02] tracking-tight text-forest sm:text-5xl lg:text-6xl">
              <MaskedLines lines={s.title.split("\n")} trigger="load" />
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.1}>
              <p className="body-text">{s.subtitle}</p>
            </Reveal>
          </div>
        </div>

        <div className="tech-grid grid grid-cols-1 border-l border-t border-line sm:grid-cols-2 lg:grid-cols-4">
          {s.items.map((item, i) => {
            const Icon = serviceIcons[i];
            return (
              <MotionLink
                key={i}
                to={`/service/${serviceSlugs[i]}`}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="service-cell group relative border-b border-r border-line p-7 md:p-8"
              >
                <div className="flex items-start justify-between">
                  <Icon
                    size={28}
                    strokeWidth={1.3}
                    className="text-terracotta transition-transform duration-500 group-hover:-translate-y-1"
                  />
                  <span className="heading-serif text-sm text-ink-soft/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="heading-serif mt-10 text-2xl text-forest md:text-[1.7rem]">
                  {item.name}
                </h3>
                <p className="overline mt-1 !text-sage">{item.tag}</p>

                <p className="body-text mt-4 text-sm">{item.desc}</p>

                <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-ink opacity-0 transition-all duration-500 group-hover:opacity-100">
                  {s.cardCta}
                  <ArrowUpRight size={16} strokeWidth={1.75} />
                </span>
              </MotionLink>
            );
          })}

          <motion.a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="service-help-cell"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="group relative flex flex-col justify-between overflow-hidden border-b border-r border-line bg-forest p-7 transition-colors duration-500 hover:bg-forest-dark md:p-8"
          >
            <MessageCircle size={28} strokeWidth={1.3} className="text-bg/80" />
            <div>
              <h3 className="heading-serif mt-10 text-2xl text-bg md:text-[1.7rem]">
                {s.helpTitle}
              </h3>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-bg/90">
                {s.helpCta}
                <ArrowUpRight size={16} strokeWidth={1.75} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};
